import * as THREE from 'three';

// Debug configuration
let DEBUG_VALIDATION = false; // Set to true to enable verbose logging
let debugSessionId = Math.random().toString(36).substring(7);
let debugCallCount = 0;

// Export function to control debugging
export function setValidationDebug(enabled) {
  DEBUG_VALIDATION = enabled;
  if (enabled) {
    debugSessionId = Math.random().toString(36).substring(7);
    debugCallCount = 0;
    console.log(`[GeometryValidation] Debug logging enabled. Session ID: ${debugSessionId}`);
  } else {
    console.log(`[GeometryValidation] Debug logging disabled.`);
  }
}

// Minimal LAMP_CONFIG for validation
const LAMP_CONFIG = {
  BASE_RADIUS: 45, // mm
  BASE_HEIGHT: 10, // mm
  FOOTPRINT_RADIUS: 30, // mm
  FOOTPRINT_HEIGHT: 150, // mm
  CLASH_DETECTION: {
    LIGHT_BASE: {
      RADIUS: 45, // mm
      HEIGHT: 10 // mm
    },
    LIGHT_GLOBE: {
      RADIUS: 15, // mm
      HEIGHT: 200 // mm
    }
  },
  PRINTABLE_AREA: {
    WIDTH: 250, // mm
    DEPTH: 250, // mm
    HEIGHT: 250 // mm
  }
};

// Create a footprint geometry from the base circle
function createFootprintGeometry(
  footprintRadius = LAMP_CONFIG.FOOTPRINT_RADIUS,
  height = LAMP_CONFIG.FOOTPRINT_HEIGHT
) {
  const group = new THREE.Group();
  const baseShape = new THREE.Shape();
  const segments = 32;
  const baseRadius = LAMP_CONFIG.BASE_RADIUS;
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = Math.cos(angle) * baseRadius;
    const y = Math.sin(angle) * baseRadius;
    if (i === 0) {
      baseShape.moveTo(x, y);
    } else {
      baseShape.lineTo(x, y);
    }
  }
  const baseExtrudeSettings = {
    steps: 1,
    depth: LAMP_CONFIG.BASE_HEIGHT,
    bevelEnabled: false
  };
  const baseGeometry = new THREE.ExtrudeGeometry(baseShape, baseExtrudeSettings);
  baseGeometry.rotateX(-Math.PI / 2);
  const footprintShape = new THREE.Shape();
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = Math.cos(angle) * footprintRadius;
    const y = Math.sin(angle) * footprintRadius;
    if (i === 0) {
      footprintShape.moveTo(x, y);
    } else {
      footprintShape.lineTo(x, y);
    }
  }
  const footprintExtrudeSettings = {
    steps: 1,
    depth: LAMP_CONFIG.FOOTPRINT_HEIGHT,
    bevelEnabled: false
  };
  const footprintGeometry = new THREE.ExtrudeGeometry(footprintShape, footprintExtrudeSettings);
  footprintGeometry.rotateX(-Math.PI / 2);
  const baseMesh = new THREE.Mesh(baseGeometry);
  const footprintMesh = new THREE.Mesh(footprintGeometry);
  group.add(baseMesh);
  group.add(footprintMesh);
  return group;
}

// Check for clashes between a geometry and a footprint
function checkFootprintClash(geometry, footprintGeometry) {
  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;
  const lightBaseRadius = LAMP_CONFIG.CLASH_DETECTION.LIGHT_BASE.RADIUS;
  const lightBaseHeight = LAMP_CONFIG.CLASH_DETECTION.LIGHT_BASE.HEIGHT;
  const lightGlobeRadius = LAMP_CONFIG.CLASH_DETECTION.LIGHT_GLOBE.RADIUS;
  const lightGlobeHeight = LAMP_CONFIG.CLASH_DETECTION.LIGHT_GLOBE.HEIGHT;
  const EPSILON = 0.001;
  for (let i = 0; i < positions.length; i += 3) {
    const vertex = new THREE.Vector3(
      positions[i],
      positions[i + 1],
      positions[i + 2]
    );
    if (Math.abs(vertex.y) < EPSILON) continue;
    const distanceFromCenter = Math.sqrt(vertex.x * vertex.x + vertex.z * vertex.z);
    if (distanceFromCenter <= lightBaseRadius && vertex.y <= lightBaseHeight) return true;
    if (distanceFromCenter <= lightGlobeRadius && vertex.y <= lightGlobeHeight) return true;
  }
  if (indices) {
    for (let i = 0; i < indices.length; i += 3) {
      const v1 = new THREE.Vector3(
        positions[indices[i] * 3],
        positions[indices[i] * 3 + 1],
        positions[indices[i] * 3 + 2]
      );
      const v2 = new THREE.Vector3(
        positions[indices[i + 1] * 3],
        positions[indices[i + 1] * 3 + 1],
        positions[indices[i + 1] * 3 + 2]
      );
      const v3 = new THREE.Vector3(
        positions[indices[i + 2] * 3],
        positions[indices[i + 2] * 3 + 1],
        positions[indices[i + 2] * 3 + 2]
      );
      if (Math.abs(v1.y) < EPSILON && Math.abs(v2.y) < EPSILON && Math.abs(v3.y) < EPSILON) continue;
      const faceCenter = new THREE.Vector3().add(v1).add(v2).add(v3).divideScalar(3);
      const distanceFromCenter = Math.sqrt(faceCenter.x * faceCenter.x + faceCenter.z * faceCenter.z);
      if (distanceFromCenter <= lightBaseRadius && faceCenter.y <= lightBaseHeight) return true;
      if (distanceFromCenter <= lightGlobeRadius && faceCenter.y <= lightGlobeHeight) return true;
    }
  }
  return false;
}

/**
 * Shared geometry validation for lamp designs.
 * @param geometry - The lamp geometry (THREE.BufferGeometry)
 * @param options - { overhangThreshold: number (degrees), height: number }
 * @returns { hasFootprintClash: boolean, hasOverhang: boolean, hasPrintableAreaClash: boolean }
 */
export function validateLampGeometry(
  geometry,
  options
) {
  const { overhangThreshold, height } = options;
  
  // Always define startTime for performance measurement
  const startTime = DEBUG_VALIDATION ? performance.now() : 0;
  
  // Debug logging
  if (DEBUG_VALIDATION) {
    debugCallCount++;
    const callId = `${debugSessionId}-${debugCallCount}`;
    
    console.group(`[GeometryValidation-${callId}] Starting validation`);
    console.log('Input parameters:', {
      overhangThreshold,
      height,
      geometryVertexCount: geometry.attributes.position.count,
      geometryHasIndex: !!geometry.index,
      geometryBoundingBox: (() => {
        geometry.computeBoundingBox();
        return geometry.boundingBox;
      })()
    });
  }
  // 1. Footprint clash
  const footprintGeometry = createFootprintGeometry(30, height);
  const hasFootprintClash = checkFootprintClash(geometry, footprintGeometry);
  
  if (DEBUG_VALIDATION) {
    console.log('Step 1 - Footprint clash:', {
      hasFootprintClash,
      lightBaseRadius: LAMP_CONFIG.CLASH_DETECTION.LIGHT_BASE.RADIUS,
      lightBaseHeight: LAMP_CONFIG.CLASH_DETECTION.LIGHT_BASE.HEIGHT,
      lightGlobeRadius: LAMP_CONFIG.CLASH_DETECTION.LIGHT_GLOBE.RADIUS,
      lightGlobeHeight: LAMP_CONFIG.CLASH_DETECTION.LIGHT_GLOBE.HEIGHT
    });
  }
  
  // 2. Overhang
  const positions = geometry.attributes.position.array;
  const normals = geometry.attributes.normal.array;
  let hasOverhang = false;
  const EPSILON = 0.001;
  const thresholdRadians = overhangThreshold * Math.PI / 180;
  let overhangVertexCount = 0;
  let maxOverhangAngle = 0;
  
  for (let i = 0; i < positions.length; i += 3) {
    const normal = { x: normals[i], y: normals[i + 1], z: normals[i + 2] };
    const position = { x: positions[i], y: positions[i + 1], z: positions[i + 2] };
    if (position.y <= EPSILON) continue;
    const norm = Math.sqrt(normal.x * normal.x + normal.y * normal.y + normal.z * normal.z);
    const normalizedNormal = {
      x: normal.x / norm,
      y: normal.y / norm,
      z: normal.z / norm
    };
    const dotProduct = Math.abs(normalizedNormal.y);
    const angle = Math.acos(dotProduct);
    const overhangAngle = (Math.PI / 2) - angle;
    
    if (overhangAngle > maxOverhangAngle) {
      maxOverhangAngle = overhangAngle;
    }
    
    if (overhangAngle > thresholdRadians) {
      hasOverhang = true;
      overhangVertexCount++;
      if (DEBUG_VALIDATION && overhangVertexCount <= 5) {
        console.log(`Overhang vertex ${overhangVertexCount}:`, {
          position,
          overhangAngleDegrees: overhangAngle * 180 / Math.PI,
          thresholdDegrees: overhangThreshold
        });
      }
    }
  }
  
  if (DEBUG_VALIDATION) {
    console.log('Step 2 - Overhang analysis:', {
      hasOverhang,
      overhangVertexCount,
      maxOverhangAngleDegrees: maxOverhangAngle * 180 / Math.PI,
      thresholdDegrees: overhangThreshold,
      totalVerticesChecked: Math.floor(positions.length / 3)
    });
  }
  
  // 3. Printable area clash
  let hasPrintableAreaClash = false;
  let printableAreaViolations = 0;
  let maxViolation = { x: 0, y: 0, z: 0 };
  
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];
    
    const violatesX = Math.abs(x) > 125;
    const violatesY = y > 250;
    const violatesZ = Math.abs(z) > 125;
    
    if (violatesX || violatesY || violatesZ) {
      hasPrintableAreaClash = true;
      printableAreaViolations++;
      
      if (Math.abs(x) > Math.abs(maxViolation.x)) maxViolation.x = x;
      if (y > maxViolation.y) maxViolation.y = y;
      if (Math.abs(z) > Math.abs(maxViolation.z)) maxViolation.z = z;
      
      if (DEBUG_VALIDATION && printableAreaViolations <= 5) {
        console.log(`Printable area violation ${printableAreaViolations}:`, {
          position: { x, y, z },
          violatesX, violatesY, violatesZ
        });
      }
    }
  }
  
  const result = { hasFootprintClash, hasOverhang, hasPrintableAreaClash };
  
  if (DEBUG_VALIDATION) {
    const endTime = performance.now();
    console.log('Step 3 - Printable area:', {
      hasPrintableAreaClash,
      printableAreaViolations,
      maxViolation,
      printableAreaLimits: { x: 125, y: 250, z: 125 }
    });
    
    console.log('Final validation result:', result);
    console.log('Performance:', {
      duration: `${(endTime - startTime).toFixed(2)}ms`,
      callId: `${debugSessionId}-${debugCallCount}`
    });
    console.groupEnd();
  }
  
  return result;
} 